// Snapshot → UI-friendly summary projection. Lives outside the hook so it can
// be unit-tested without React.
//
// Minimap and Player HUD data are served from the Rust World via WASM bindings
// (respecting fog of war, diplomacy info-gating, proper power scores).
// This module handles the snake_case → camelCase mapping layer.

import type { GameSnapshot, SceneNode, Sea, SeaState } from '@aod/protocol';
import type { MinimapSnapshot, MinimapTile, PlayerHudEntry } from '@aod/ui';
import type { Archetype, DoctrineState } from '../../ui/DoctrinePanel/index.js';
import { setCalendarYearTicks } from '../gameTime.js';
import { cropMinimapToBoard, cropMinimapToLand } from './minimapBounds.js';
import type {
  PlayerVictoryProgress,
  VictoryPath,
  VictoryProgressState,
  VictoryState,
} from './types.js';

// ---------------------------------------------------------------------------
// Raw WASM types (serde snake_case serialization)
// ---------------------------------------------------------------------------

interface RawMinimapSnapshot {
  readonly tiles: RawMinimapTile[];
  readonly min_q: number;
  readonly max_q: number;
  readonly min_r: number;
  readonly max_r: number;
}

interface RawMinimapTile {
  readonly q: number;
  readonly r: number;
  readonly biome: string;
  /**
   * Controlling player; absent/null = unclaimed (0 is the human, not "nobody").
   * The WASM serializer maps `None` to JS `undefined`, so it is normalised to
   * `null` by `mapMinimap`.
   */
  readonly controlled_by?: number | null;
  readonly visibility: 'visible' | 'explored' | 'fog';
  readonly has_city: boolean;
  readonly has_unit: boolean;
  /** Edge-river mask in the sim's AXIAL_DIRS bit order; 0 under fog. */
  readonly river_edges?: number;
  readonly terrain_level?: number;
}

interface RawPlayerHudEntry {
  readonly player_id: number;
  readonly name: string;
  readonly color: string;
  readonly power_score: number;
  readonly info_level: 'Full' | 'Estimate' | 'Minimal';
  readonly breakdown?: RawPowerBreakdown;
}

interface RawPowerBreakdown {
  readonly military: number;
  readonly economic: number;
  readonly prestige: number;
  readonly territory: number;
  readonly doctrine: number;
}

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

interface RawPlayerState {
  readonly playerIndex: number;
  readonly isHuman: boolean;
  readonly unitAttack?: number;
  readonly doctrine: {
    readonly spent: ReadonlyArray<number>;
    readonly available: number;
    readonly nextGrantTick: number;
    readonly dominant: Archetype | null;
  };
  readonly treasury: {
    readonly food: number;
    readonly industry: number;
    readonly knowledge: number;
    readonly faith: number;
    readonly gold: number;
  };
}

export interface TreasurySummary {
  readonly food: number;
  readonly industry: number;
  readonly knowledge: number;
  readonly faith: number;
  readonly gold: number;
}

const ZERO_TREASURY: TreasurySummary = {
  food: 0,
  industry: 0,
  knowledge: 0,
  faith: 0,
  gold: 0,
};

export interface FaithCrisisState {
  readonly tension: number;
  readonly level: string;
  /** Crisis events that have already fired this cycle. */
  readonly firedEvents: readonly string[];
}

export interface ResearchState {
  readonly active: boolean;
  readonly remainingTicks: number;
  readonly industryBonus: number;
  readonly knowledgeBonus: number;
}

const SEAS = ['rim', 'landlocked', 'open', 'archipelago'] as const;

/** The sea axis the sim published (`state.globals.sea`): the mode and the
 * frame the board was generated with. A board without it is today's rim
 * board, and a frame without rings falls back to the mode's own shape. */
export function parseSeaState(raw: unknown): SeaState {
  const g = (raw ?? {}) as {
    mode?: string;
    oceanRings?: number;
    coastRings?: number;
    wallRings?: number;
  };
  const mode = (SEAS as readonly string[]).includes(g.mode ?? '') ? (g.mode as Sea) : 'rim';
  const rings = (n: unknown, fallback: number): number =>
    typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.round(n) : fallback;
  const fallback =
    mode === 'landlocked'
      ? { oceanRings: 0, coastRings: 0, wallRings: 2 }
      : { oceanRings: 1, coastRings: 1, wallRings: 0 };
  return {
    mode,
    frame: {
      oceanRings: rings(g.oceanRings, fallback.oceanRings),
      coastRings: rings(g.coastRings, fallback.coastRings),
      wallRings: rings(g.wallRings, fallback.wallRings),
    },
  };
}

export interface SnapshotSummary {
  /** Map climate (terrain-levels D11). */
  readonly climate: 'temperate' | 'arctic' | 'tropical' | 'desert';
  /** The sea axis (sea-design §3 A2) — the mode and the board's frame. */
  readonly sea: SeaState;
  readonly tileCount: number;
  readonly cityCount: number;
  readonly localPlayer: DoctrineState | null;
  readonly localTreasury: TreasurySummary;
  readonly researchState: ResearchState | null;
  readonly faithCrisis: FaithCrisisState | null;
  readonly victory: VictoryState | null;
  readonly victoryProgress: VictoryProgressState | null;
  readonly playerHudEntries: PlayerHudEntry[];
  readonly minimapSnapshot: MinimapSnapshot | null;
}

// ---------------------------------------------------------------------------
// Main summarise entry point (localPlayer + treasury + victory only)
// ---------------------------------------------------------------------------

export function summarize(
  snapshot: GameSnapshot,
  localPlayerIndex: number,
): Omit<SnapshotSummary, 'playerHudEntries' | 'minimapSnapshot'> {
  const children = snapshot.scene.children ?? [];

  let tileCount = 0;
  let cityCount = 0;

  for (const node of children) {
    if (node.type === 'tile') {
      tileCount++;
    } else if (node.type === 'city') {
      cityCount++;
    }
  }

  let localPlayer: DoctrineState | null = null;
  let localTreasury: TreasurySummary = ZERO_TREASURY;
  let researchState: ResearchState | null = null;
  let faithCrisis: FaithCrisisState | null = null;
  const globals = snapshot.state.globals as
    | {
        players?: ReadonlyArray<RawPlayerState>;
        victory?: { winner: number; path: VictoryPath } | null;
      }
    | undefined;
  const players = globals?.players ?? [];
  for (const p of players) {
    if (p.playerIndex === localPlayerIndex) {
      localPlayer = {
        spent: p.doctrine.spent,
        available: p.doctrine.available,
        nextGrantTick: p.doctrine.nextGrantTick,
        dominant: p.doctrine.dominant ?? null,
        unitAttack: p.unitAttack ?? 4,
      };
      localTreasury = {
        food: p.treasury.food,
        industry: p.treasury.industry,
        knowledge: p.treasury.knowledge,
        faith: p.treasury.faith,
        gold: p.treasury.gold,
      };
      const research = (p as unknown as Record<string, unknown>).research as
        | ResearchState
        | undefined
        | null;
      if (research?.active) {
        researchState = research;
      }
      const fc = (p as unknown as Record<string, unknown>).faithCrisis as
        | FaithCrisisState
        | undefined
        | null;
      if (fc) {
        faithCrisis = fc;
      }
      break;
    }
  }

  const CLIMATES = ['temperate', 'arctic', 'tropical', 'desert'] as const;
  type Climate = (typeof CLIMATES)[number];
  const rawClimate = (globals as { climate?: string } | undefined)?.climate;
  const climate = (CLIMATES as readonly string[]).includes(rawClimate ?? '')
    ? (rawClimate as Climate)
    : 'temperate';

  const sea = parseSeaState((globals as { sea?: unknown } | undefined)?.sea);

  // The calendar axis (generations design §4a): real minutes per in-game
  // year, published by the sim. Every snapshot — a new match, a restored
  // save, a multiplayer frame — passes through here, so this is the one
  // place the client calendar is set (gameTime's singleton).
  setCalendarYearTicks(
    typeof (globals as { yearMinutes?: unknown } | undefined)?.yearMinutes === 'number'
      ? (globals as { yearMinutes: number }).yearMinutes * 600
      : null,
  );

  const victory: VictoryState | null = globals?.victory
    ? { winner: globals.victory.winner, path: globals.victory.path }
    : null;

  const rawVp = globals as Record<string, unknown> | undefined;
  const victoryProgress: VictoryProgressState | null =
    rawVp?.victoryProgress && rawVp?.victoryConfig
      ? {
          progress: rawVp.victoryProgress as Record<number, PlayerVictoryProgress>,
          config: rawVp.victoryConfig as VictoryProgressState['config'],
          empireTick: (rawVp.empireTick as number) ?? 0,
        }
      : null;

  return {
    tileCount,
    cityCount,
    localPlayer,
    localTreasury,
    researchState,
    faithCrisis,
    victory,
    climate,
    sea,
    victoryProgress,
  };
}

// ---------------------------------------------------------------------------
// Mapping: raw WASM types → @aod/ui types
// ---------------------------------------------------------------------------

/** Map raw WASM player HUD entries to the camelCase UI type. */
export function mapPlayerHud(raw: RawPlayerHudEntry[]): PlayerHudEntry[] {
  const INFO_LEVEL_MAP: Record<string, PlayerHudEntry['infoLevel']> = {
    Full: 'full',
    Estimate: 'estimate',
    Minimal: 'minimal',
  };

  return raw.map((entry) => ({
    playerId: entry.player_id,
    name: entry.name,
    color: `#${entry.color}`,
    powerScore: entry.power_score,
    infoLevel: INFO_LEVEL_MAP[entry.info_level] ?? 'minimal',
    breakdown: entry.breakdown
      ? {
          military: entry.breakdown.military,
          economic: entry.breakdown.economic,
          prestige: entry.breakdown.prestige,
          territory: entry.breakdown.territory,
          doctrine: entry.breakdown.doctrine,
        }
      : undefined,
  }));
}

/** Map raw WASM minimap snapshot to the camelCase UI type.
 *
 * An open board's ocean ring is wide and flat, so its minimap shows the
 * land's own box plus a ring of sea (sea-design §3 A3) rather than the
 * whole board. */
export function mapMinimap(raw: RawMinimapSnapshot, sea?: SeaState): MinimapSnapshot {
  const cropped = cropMinimapToBoard({
    tiles: raw.tiles.map((t) => ({
      q: t.q,
      r: t.r,
      biome: t.biome,
      // Unclaimed is `null` (the WASM serializer hands over `undefined`).
      controlledBy: t.controlled_by ?? null,
      visibility: t.visibility,
      hasCity: t.has_city,
      hasUnit: t.has_unit,
      riverEdges: t.river_edges ?? 0,
      terrainLevel: t.terrain_level ?? 0,
    })),
    minQ: raw.min_q,
    maxQ: raw.max_q,
    minR: raw.min_r,
    maxR: raw.max_r,
  });
  return sea?.mode === 'open' ? cropMinimapToLand(cropped) : cropped;
}
